import _ from "lodash";
import React from "react";
import classes from "./DynamicChildren.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import FieldComponent from "../FieldComponent";
import EmptyField from "UIComponents/EmptyField";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import LabelComponent from "Projects/components/StepContainer/components/LabelComponent";
import {
  createNestedPlannerFields,
  deletePlannerField,
} from "UnitPlans/modules/PlannerFieldThunks";
import { getFieldContextFromParentType } from "UnitPlans/modules/Utils";
import {
  AddCircleOutlined,
  DeleteOutlined,
  StarFilled,
} from "@toddle-edu/ds-icons";
import { Button, IconButton, Dialog, Tooltip } from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import { getProjectDetailsFromCache } from "Projects/modules/ProjectGraphqlHelpers";

const DIALOGS_INFO = {
  DELETE: {
    title: "common:delete_with_label",
    message: "common:delete_msg_with_label",
    confirmButtonText: "common:delete",
    cancelButtonText: "common:cancel",
  },
  DELETE_ARTWORK: {
    title: "common:delete_with_label",
    message: "project:delete_artworks_message",
    confirmButtonText: "common:delete",
    cancelButtonText: "common:cancel",
  },
};

const IconMap = {
  StarFilled: <StarFilled />,
};

const styles = {
  labelContainerStyle: {
    marginBottom: 0,
  },
  containerStyle: {
    marginTop: 0,
    gridGap: 0,
  },
};

class DynamicChildren extends UIBaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      showLoader: false,
      showDisabledState: false,
      currentDialogAction: null,
      currentSelectedFieldId: null,
      currentDeleteLabel: "",
    };
  }

  fieldContext = getFieldContextFromParentType({
    parentType: this.props.fieldContextParentType,
  });

  handleArtworkUpdate = ({
    inputParams,
    extraParams,
    projectId,
    projectGroupType,
    academicYearId,
    updateInputField,
  }) => {
    const key = _.first(_.keys(inputParams));
    const projectData = getProjectDetailsFromCache({
      projectId,
      projectGroupType,
      academicYearId,
    });
    const fields = _.get(projectData, "node.fields", []);

    _.forEach(fields, item => {
      const { uid, value } = item;
      if (_.includes(uid, "artwork_is_selected")) {
        let fieldSpecificUpdateParams;
        if (value) {
          // If the artwork is currently selected (value is true), deselect it.
          fieldSpecificUpdateParams = { [uid]: false };
        } else if (uid === key) {
          // If this field\'s uid matches the key that triggered the update (and value is not true), select it.
          fieldSpecificUpdateParams = { [uid]: true };
        }
        // This call preserves the original behavior:
        // updateInputField is called even if fieldSpecificUpdateParams is undefined,
        // as long as _.includes(uid, "artwork_is_selected") is true.
        updateInputField(fieldSpecificUpdateParams, extraParams);
      }
    });
  };

  updateInputFieldLocally = ({ params: inputParams, extraParams }) => {
    const {
      updateInputField,
      projectId,
      projectGroupType,
      academicYearId,
    } = this.props;

    const key = _.first(_.keys(inputParams));
    const isArtworkRelatedKey = _.includes(key, "artwork_is_selected");

    if (isArtworkRelatedKey && inputParams[key]) {
      this.handleArtworkUpdate({
        inputParams,
        extraParams,
        projectId,
        projectGroupType,
        academicYearId,
        updateInputField,
      });
    } else {
      // If not an artwork-related update, or the triggering artwork is being deselected (inputParams[key] is false),
      // pass the original params through.
      updateInputField(inputParams, extraParams);
    }
  };

  getCreateValue = () => {
    const { defaultChildCreateValue, fieldKey } = this.props;

    switch (fieldKey) {
      default:
        return defaultChildCreateValue;
    }
  };

  addChildren = async () => {
    const {
      fieldId,
      createNestedPlannerFields,
      parentId,
      parentType,
    } = this.props;
    this.setState({ showDisabledState: true });

    await createNestedPlannerFields({
      parentFieldId: fieldId,
      parentId,
      parentType,
      value: this.getCreateValue(),
    });

    this.setState({ showDisabledState: false });
  };

  onClickDelete = ({
    fieldId,
    currentDeleteLabelValue,
    deleteModalType = "DELETE",
  }) => {
    this.setState({
      currentSelectedFieldId: fieldId,
      currentDialogAction: deleteModalType,
      currentDeleteLabel: currentDeleteLabelValue,
    });
  };

  deleteChildren = async () => {
    const { deletePlannerField, parentId, parentType } = this.props;

    const { currentSelectedFieldId } = this.state;

    this.setState({ showLoader: true });
    await deletePlannerField({
      fieldId: currentSelectedFieldId,
      parentId,
      parentType,
    });
    this.setState({ showLoader: false });

    this.closeDialogue();
  };

  closeDialogue = () => {
    this.setState({ currentDialogAction: null, currentSelectedFieldId: null });
  };

  renderIcon = (icon, variant) => {
    const { t } = this.props;
    const iconComponent = IconMap[icon];
    return iconComponent ? (
      <Tooltip tooltip={t("project:selected_artwork")} placement="right">
        {React.cloneElement(iconComponent, {
          variant: variant,
        })}
      </Tooltip>
    ) : null;
  };

  getExtraLabelComponent = ({ childList, config }) => {
    const { getFieldValue } = this.props;

    const { pseudoFieldUid: pseudoFieldUID, icon, variant } = _.get(
      config,
      "extraLabelComponentConfig",
      {}
    );

    // Early exit if required configuration or data is missing
    if (!pseudoFieldUID || !icon || !childList || !getFieldValue) {
      return null;
    }

    // Find the field whose ID includes the pseudoFieldUID
    const extraLabelField = _.find(
      childList,
      item => item?.id && _.includes(item.id, pseudoFieldUID)
    );
    if (extraLabelField) {
      const extraLabelFieldValue = getFieldValue({
        type: extraLabelField.type,
        fielduid: extraLabelField.id,
      });

      // If the value is strictly true, render the icon
      if (extraLabelFieldValue === true) {
        return this.renderIcon(icon, variant);
      }
    }

    // Return null if the field is not found or its value is not true
    return null;
  };

  getExtraRightButtonComponent = ({
    fieldId,
    childrenListCount,
    currentDeleteLabelValue,
    disabled,
    deleteModalType,
  }) => {
    const { testId } = this.props;
    return (
      <IconButton
        icon={<DeleteOutlined variant="subtle" />}
        size={"medium"}
        variant={"neutral"}
        type={"plain"}
        disabled={disabled || (childrenListCount < 2 ? true : false)}
        onClick={() =>
          this.onClickDelete({
            fieldId,
            currentDeleteLabelValue,
            deleteModalType,
          })
        }
        testId={`${testId}-delete`}
      />
    );
  };

  renderEditEmpty = () => {
    const { emptyText, emptyContainerStyle } = this.props;
    return (
      !!emptyText && (
        <EmptyField containerStyle={emptyContainerStyle} title={emptyText} />
      )
    );
  };

  renderEdit = () => {
    const {
      mode,
      childrenContainerStyle,
      childrenList,
      onFocusInputField,
      onBlurInputField,
      t,
      getOptions,
      getFieldValue,
      getGraphqlEnhancerQueryInfo,
      getUpdatedConfig,
      helpEnabledFields,
      getIsHelpActive,
      onHelpClick,
      updateSelectedTemplate,
      fieldContextParentType,
      showAddButton,
      addMaxChildrenCount,
      addButtonLabel,
      disabled,
      showSeparatorBetweenDynamicChildren,
      getIsCommentActive,
      onCommentClick,
      getShowComment,
      testId,
      displayDisabledAsActive,
      template,
      projectId,
      projectGroupType,
      academicYearId,
      projectGroupId,
      projectDetails,
    } = this.props;

    const currentDialogAction = this.state.currentDialogAction;

    const { showLoader, showDisabledState, currentDeleteLabel } = this.state;

    const FieldContext = this.fieldContext;

    //to-do Wrapper component and JSON stringify/parser
    return (
      <FieldContext.Consumer>
        {({ fields }) => {
          return (
            <div className={classes.container} style={childrenContainerStyle}>
              {_.map(childrenList, (field, index) => {
                const {
                  config = {},
                  type,
                  childrenList: childList = [],
                  id,
                  inputStyle,
                } = field;

                const fieldDetails = _.find(fields, { uid: id });

                const fielduid = _.get(fieldDetails, "uid");

                const totalCommentCount = _.get(
                  fieldDetails,
                  "comments.totalCount",
                  0
                );

                const unreadCount = _.get(
                  fieldDetails,
                  "comments.unreadCountV2",
                  0
                );

                const value = getFieldValue({
                  type,
                  fielduid,
                });

                const graphqlEnhancerQueryInfo = getGraphqlEnhancerQueryInfo({
                  fielduid,
                });

                const fieldId = _.get(fieldDetails, "id");

                const {
                  label = "",
                  subtext = "",
                  labelStyle = {},
                  subTextStyle = {},
                  childContainerStyle = {},
                  readLabelFromValue = false,
                  emptyText,
                  placeholder,
                  isPageHeader,
                  isSectionHeader,
                  fieldStyle = {},
                  showDelete = false,
                  deleteModalType,
                  allowComment,
                  showComment = false,
                  customLabelStyles = {},
                } = config;

                const labelValue = _.get(value, "localeLabel", "");

                const localeSubtext = _.get(value, "localeSubtext", "");
                const showCountInLocale = _.get(
                  value,
                  "showCountInLocale",
                  false
                );
                const localeLabelValue = showCountInLocale
                  ? t(labelValue, { count: index + 1 })
                  : t(labelValue);

                const updateConfig = getUpdatedConfig({
                  config,
                  fielduid,
                  type,
                });
                const helpText = _.find(helpEnabledFields, eachField => {
                  return _.get(eachField, "uid", "") === id;
                });
                const helperTextId = _.get(helpText, "helpText.id", "");

                const labelComponent = (
                  <LabelComponent
                    label={label}
                    subText={subtext}
                    showHelper={!_.isEmpty(helpText)}
                    isHelpActive={getIsHelpActive({ fielduid: id })}
                    onHelpClick={() =>
                      onHelpClick({
                        fieldKey: id,
                        helperTextId: helperTextId,
                        id: fieldId,
                      })
                    }
                    isPageHeader={isPageHeader}
                    isSectionHeader={isSectionHeader}
                    isFieldHeader={!isPageHeader && !isSectionHeader}
                    showComment={getShowComment({ allowComment: showComment })}
                    onCommentClick={() =>
                      onCommentClick({
                        id: fieldDetails?.id,
                        uid: id,
                      })
                    }
                    totalCommentCount={totalCommentCount}
                    isCommentActive={getIsCommentActive({
                      fieldId: fieldDetails?.id,
                    })}
                    customLabelStyles={customLabelStyles}
                  />
                );

                return (
                  <div
                    className={classes.fieldContainer}
                    style={fieldStyle}
                    key={fielduid}
                  >
                    {showSeparatorBetweenDynamicChildren && index > 0 && (
                      <div className={classes.separator} />
                    )}
                    {readLabelFromValue && (
                      <LabelComponent
                        extraLabelComponent={this.getExtraLabelComponent({
                          childList,
                          config,
                        })}
                        label={localeLabelValue}
                        subText={localeSubtext && t(localeSubtext)}
                        showComment={getShowComment({ allowComment })}
                        totalCommentCount={totalCommentCount}
                        unreadCommentCount={unreadCount}
                        labelStyle={labelStyle}
                        subTextStyle={subTextStyle}
                        isPageHeader={isPageHeader}
                        isSectionHeader={isSectionHeader}
                        isFieldHeader={!isPageHeader && !isSectionHeader}
                        onCommentClick={() =>
                          onCommentClick({
                            id: fieldDetails?.id,
                            uid: id,
                          })
                        }
                        isCommentActive={getIsCommentActive({
                          fieldId: fieldDetails?.id,
                        })}
                        extraRightButtonComponent={
                          showDelete
                            ? this.getExtraRightButtonComponent({
                                fieldId,
                                childrenListCount: _.size(childrenList),
                                currentDeleteLabelValue: localeLabelValue,
                                disabled,
                                deleteModalType,
                              })
                            : null
                        }
                        showDelete={showDelete}
                      />
                    )}
                    <div
                      className={classes.childContainer}
                      style={childContainerStyle}
                    >
                      {/* to-do locked  */}
                      <FieldComponent
                        {...updateConfig}
                        value={value}
                        placeholder={placeholder}
                        key={id}
                        labelComponent={labelComponent}
                        customRef={this.setFieldRef}
                        onFocusInputField={onFocusInputField}
                        onBlurInputField={onBlurInputField}
                        type={type}
                        fieldKey={id}
                        fieldId={fieldId}
                        updateInputField={this.updateInputFieldLocally}
                        mode={mode}
                        childrenList={childList}
                        emptyText={emptyText}
                        inputStyle={inputStyle}
                        getOptions={getOptions}
                        getFieldValue={getFieldValue}
                        graphqlEnhancerQueryInfo={graphqlEnhancerQueryInfo}
                        getGraphqlEnhancerQueryInfo={
                          getGraphqlEnhancerQueryInfo
                        }
                        getUpdatedConfig={getUpdatedConfig}
                        helpEnabledFields={helpEnabledFields}
                        getIsHelpActive={getIsHelpActive}
                        onHelpClick={onHelpClick}
                        updateSelectedTemplate={updateSelectedTemplate}
                        fieldContextParentType={fieldContextParentType}
                        getShowComment={getShowComment}
                        getIsCommentActive={getIsCommentActive}
                        testId={`${testId}-${id}`}
                        dsVersion="2.0"
                        labelContainerStyle={{
                          ...styles.labelContainerStyle,
                          ...config?.styles?.labelContainerStyle,
                        }}
                        containerStyle={{
                          ...styles.containerStyle,
                          ...config?.styles?.containerStyle,
                        }}
                        displayDisabledAsActive={displayDisabledAsActive}
                        template={template}
                        projectId={projectId}
                        projectGroupType={projectGroupType}
                        academicYearId={academicYearId}
                        projectGroupId={projectGroupId}
                        projectDetails={projectDetails}
                      />
                    </div>
                  </div>
                );
              })}

              {showAddButton && (
                <div className={classes.addChildren}>
                  <Button
                    icon={<AddCircleOutlined />}
                    type="outlined"
                    variant="neutral"
                    size="medium"
                    onClick={this.addChildren}
                    disabled={
                      disabled ||
                      showDisabledState ||
                      (!_.isNull(addMaxChildrenCount) &&
                        _.size(childrenList) >= addMaxChildrenCount)
                    }
                  >
                    {t(
                      showDisabledState
                        ? "common:adding_with_label"
                        : "common:add_with_label",
                      {
                        label: t(addButtonLabel),
                      }
                    )}
                  </Button>
                </div>
              )}

              {currentDialogAction ? (
                <Dialog
                  title={t(DIALOGS_INFO[currentDialogAction].title, {
                    label: currentDeleteLabel,
                  })}
                  onClose={this.closeDialogue}
                  buttonOneText={t(
                    DIALOGS_INFO[currentDialogAction].cancelButtonText
                  )}
                  buttonTwoText={t(
                    DIALOGS_INFO[currentDialogAction].confirmButtonText
                  )}
                  onButtonOneClick={this.closeDialogue}
                  onButtonTwoClick={this.deleteChildren}
                  message={t(DIALOGS_INFO[currentDialogAction].message, {
                    label: currentDeleteLabel,
                  })}
                  dsVersion="2.0"
                />
              ) : null}

              {showLoader && <FullScreenLoader />}
            </div>
          );
        }}
      </FieldContext.Consumer>
    );
  };

  shouldShowViewEmpty = () => {
    return false;
  };

  shouldShowEditEmpty = () => {
    const { childrenList } = this.props;
    return _.isEmpty(childrenList);
  };

  renderView = () => {
    return this.renderEdit();
  };
}

DynamicChildren.defaultProps = {
  ...UIBaseComponent.defaultProps,
  childrenContainerStyle: {},
  showAddButton: false,
  defaultChildCreateValue: {},
  addMaxChildrenCount: null,
  addButtonLabel: "common:another",
  showSeparatorBetweenDynamicChildren: false,
};

DynamicChildren.propTypes = {
  ...UIBaseComponent.propTypes,
  showAddButton: PropTypes.bool,
  defaultChildCreateValue: PropTypes.object,
  addMaxChildrenCount: PropTypes.number,
  addButtonLabel: PropTypes.string,
  showSeparatorBetweenDynamicChildren: PropTypes.bool,
};

const mapActionCreators = {
  createNestedPlannerFields,
  deletePlannerField,
};

export default compose(
  I18nHOC({ resource: ["common", "projectTemplate"] }),
  connect(null, mapActionCreators)
)(DynamicChildren);
